import React, { useState, useMemo, useRef, useEffect } from 'react';
import { TimelineShipment } from '@/features/calendar/hooks/useTimelineShipments';
// Timeline Component with isolated hover state
export const TimelineComponent: React.FC<{
  shipmentEvents: TimelineShipment[];
  onShipmentClick: (shipment: TimelineShipment) => void;
}> = ({ shipmentEvents, onShipmentClick }) => {
  const [hoverPosition, setHoverPosition] = useState<{
    x: number;
    date: Date;
  } | null>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const parentScrollRef = useRef<HTMLDivElement>(null);

  const timelineData = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const startOfYear = new Date(currentYear, 0, 1);
    const startDay = startOfYear.getDay();
    const mondayOffset = startDay === 0 ? -6 : 1 - startDay;
    const timelineStart = new Date(startOfYear);
    timelineStart.setDate(startOfYear.getDate() + mondayOffset);

    const endOfNextYear = new Date(currentYear + 2, 0, 1);
    const totalWeeks = Math.ceil((endOfNextYear.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24 * 7));

    const periods: { date: Date; label: string; weekNumber: number }[] = [];
    for (let w = 0; w < totalWeeks; w++) {
      const weekStart = new Date(timelineStart);
      weekStart.setDate(timelineStart.getDate() + w * 7);
      const yearStart = new Date(weekStart.getFullYear(), 0, 1);
      const weekNumber = Math.ceil(((weekStart.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24) + 1) / 7);

      periods.push({
        date: weekStart,
        label: weekStart.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        weekNumber: weekNumber,
      });
    }

    const totalDays = (endOfNextYear.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24);
    return {
      periods,
      startOfPeriod: timelineStart,
      endOfPeriod: endOfNextYear,
      totalDays,
    };
  }, []);

  const visibleShipments = useMemo(() => {
    return shipmentEvents.filter((shipment) => {
      const shipmentStart = shipment.startDate;
      const shipmentEnd = shipment.endDate;
      return shipmentStart <= timelineData.endOfPeriod && shipmentEnd >= timelineData.startOfPeriod;
    });
  }, [shipmentEvents, timelineData]);

  // Auto-scroll to today
  useEffect(() => {
    const container = parentScrollRef.current;
    if (container && timelineData.periods.length > 0) {
      setTimeout(() => {
        const today = new Date();
        const daysSinceStart = (today.getTime() - timelineData.startOfPeriod.getTime()) / (1000 * 60 * 60 * 24);
        const totalWidth = timelineData.periods.length * 120;
        const scrollPosition = (daysSinceStart / timelineData.totalDays) * totalWidth;
        container.scrollTo({
          left: Math.max(0, scrollPosition - container.clientWidth / 2),
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [timelineData]);

  // Scroll sync
  useEffect(() => {
    const parentContainer = parentScrollRef.current;
    const timelineContainer = timelineContainerRef.current;
    const headerContainer = headerContainerRef.current;

    if (!parentContainer || !timelineContainer || !headerContainer) return;

    const handleParentScroll = () => {
      const scrollLeft = parentContainer.scrollLeft;
      timelineContainer.scrollLeft = scrollLeft;
      headerContainer.scrollLeft = scrollLeft;
    };

    parentContainer.addEventListener('scroll', handleParentScroll);
    return () => parentContainer.removeEventListener('scroll', handleParentScroll);
  }, []);

  const calculateBarStyle = (shipment: TimelineShipment) => {
    const totalWidth = timelineData.periods.length * 120;
    const shipmentStartMs = Math.max(shipment.startDate.getTime(), timelineData.startOfPeriod.getTime());
    const shipmentEndMs = Math.min(shipment.endDate.getTime(), timelineData.endOfPeriod.getTime());
    const startOffset = (shipmentStartMs - timelineData.startOfPeriod.getTime()) / (1000 * 60 * 60 * 24);
    const duration = (shipmentEndMs - shipmentStartMs) / (1000 * 60 * 60 * 24);
    const left = (startOffset / timelineData.totalDays) * totalWidth;
    const width = Math.max((duration / timelineData.totalDays) * totalWidth, 10);
    return { left: `${left}px`, width: `${width}px` };
  };

  const getCurrentDayPosition = () => {
    const today = new Date();
    if (today < timelineData.startOfPeriod || today > timelineData.endOfPeriod) return null;
    const totalWidth = timelineData.periods.length * 120;
    const dayOffset = (today.getTime() - timelineData.startOfPeriod.getTime()) / (1000 * 60 * 60 * 24);
    return (dayOffset / timelineData.totalDays) * totalWidth;
  };

  const getCarrierColor = (carrier: string, hasEvents: boolean) => {
    const carrierColors: Record<string, string> = {
      Maersk: '#1e40af', // Dark blue - better contrast than original
      MSC: '#d97706', // Dark orange - much better contrast than yellow
      'Hapag-Lloyd': '#3730a3', // Deep indigo - professional, non-error color
      ZIM: '#059669', // Emerald green - good contrast
      'CMA CGM': '#2563eb', // Bright blue - better contrast
      OOCL: '#0d9488', // Teal - calming, non-error color
      COSCO: '#92400e', // Bronze - warm, non-error color
      Evergreen: '#166534', // Dark green - distinguishable from ZIM
      'Yang Ming': '#c2410c', // Dark orange - good contrast
      ONE: '#6b21a8', // Purple - good contrast
      PIL: '#be185d', // Magenta - clearly different from ONE
      'Wan Hai': '#db2777', // Pink - good contrast
    };

    const exactMatch = carrierColors[carrier];
    if (exactMatch) return hasEvents ? exactMatch : `${exactMatch}80`;

    for (const [carrierName, color] of Object.entries(carrierColors)) {
      if (
        carrier.toLowerCase().includes(carrierName.toLowerCase()) ||
        carrierName.toLowerCase().includes(carrier.toLowerCase())
      ) {
        return hasEvents ? color : `${color}80`;
      }
    }
    return hasEvents ? '#6b7280' : '#6b728080';
  };

  const scrollToToday = () => {
    const today = new Date();
    const container = parentScrollRef.current;
    if (container) {
      const daysSinceStart = (today.getTime() - timelineData.startOfPeriod.getTime()) / (1000 * 60 * 60 * 24);
      const scrollPosition = (daysSinceStart / timelineData.totalDays) * container.scrollWidth;
      container.scrollTo({
        left: scrollPosition - container.clientWidth / 2,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full overflow-auto">
      <div ref={parentScrollRef} className="overflow-x-auto relative" style={{ scrollbarWidth: 'thin' }}>
        <div style={{ minWidth: `${timelineData.periods.length * 120}px` }}>
          {/* Headers */}
          <div ref={headerContainerRef} className="border-b border-gray-200 bg-gray-50 overflow-hidden z-10">
            <div className="flex">
              {timelineData.periods.map((period, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 p-3 text-center border-r border-gray-200 last:border-r-0"
                  style={{ width: '120px' }}
                >
                  <div className="text-xs font-medium text-gray-600">{period.label}</div>
                  <div className="text-xs text-gray-400 mt-1">W{period.weekNumber}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div ref={timelineContainerRef} className="overflow-hidden">
            <div
              className="relative"
              style={{
                minHeight: `${Math.max(visibleShipments.length * 40 + 40, 200)}px`,
                width: `${timelineData.periods.length * 120}px`,
              }}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const relativeX = e.clientX - rect.left;
                const totalWidth = timelineData.periods.length * 120;
                const percentage = relativeX / totalWidth;
                const dayOffset = percentage * timelineData.totalDays;
                const hoverDate = new Date(timelineData.startOfPeriod);
                hoverDate.setDate(hoverDate.getDate() + Math.floor(dayOffset));
                setHoverPosition({ x: relativeX, date: hoverDate });
              }}
              onMouseLeave={() => setHoverPosition(null)}
            >
              {/* Hover Line */}
              {hoverPosition && (
                <div
                  className="absolute top-0 bottom-0 pointer-events-none z-30 border-l-2 border-dashed border-blue-500"
                  style={{ left: `${hoverPosition.x}px` }}
                >
                  <div
                    className="absolute bg-blue-500 text-white text-xs px-3 py-1 rounded whitespace-nowrap z-40 -translate-x-1/2"
                    style={{ top: '10px' }}
                  >
                    {hoverPosition.date.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                </div>
              )}

              {/* Today Line */}
              {getCurrentDayPosition() !== null && (
                <div
                  className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-500 z-20"
                  style={{ left: `${getCurrentDayPosition()}px` }}
                >
                  <div
                    className="absolute bg-red-500 text-white text-xs px-2 py-1 rounded z-40 -translate-x-1/2"
                    style={{ top: '10px' }}
                  >
                    Today
                  </div>
                </div>
              )}

              {/* Grid Lines */}
              {timelineData.periods.map((_, index) => (
                <div
                  key={index}
                  className="absolute top-0 bottom-0 w-px bg-gray-200"
                  style={{ left: `${(index + 1) * 120}px` }}
                />
              ))}

              {/* Shipment Bars */}
              {visibleShipments.map((shipment, index) => {
                const style = calculateBarStyle(shipment);
                const color = getCarrierColor(shipment.carrier, shipment.eventsCount > 0);
                const origin = shipment.originLocation || 'Unknown';
                const destination = shipment.destinationLocation || 'Unknown';

                return (
                  <div
                    key={shipment.id}
                    className="absolute flex items-center"
                    style={{
                      top: `${20 + index * 40}px`,
                      height: '32px',
                      ...style,
                    }}
                  >
                    <div
                      className="h-6 rounded-sm flex items-center px-3 text-xs font-medium text-white shadow-sm hover:shadow-md transition-all cursor-pointer relative group"
                      style={{ backgroundColor: color, width: '100%' }}
                      onClick={() => onShipmentClick(shipment)}
                    >
                      <span className="truncate flex-1">{shipment.title}</span>
                      <span className="text-xs opacity-90 ml-2 truncate">
                        {origin.split(' ')[0]} → {destination.split(' ')[0]}
                      </span>
                      <span className="text-xs opacity-80 ml-2">{shipment.carrier}</span>
                      <span className="text-xs opacity-70 ml-2">
                        {shipment.endDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      {shipment.eventsCount > 0 && (
                        <span className="ml-2 w-2 h-2 bg-white rounded-full opacity-90 animate-pulse"></span>
                      )}

                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-30 whitespace-nowrap shadow-lg">
                        <div className="font-semibold">{shipment.title}</div>
                        <div className="text-gray-300">{shipment.project?.name}</div>
                        <div className="mt-1">
                          <div>
                            <span className="text-gray-400">Route:</span> {origin} → {destination}
                          </div>
                          <div>
                            <span className="text-gray-400">Carrier:</span> {shipment.carrier}
                          </div>
                          <div>
                            <span className="text-gray-400">ETD:</span> {shipment.startDate.toLocaleDateString()}
                          </div>
                          <div>
                            <span className="text-gray-400">ETA:</span> {shipment.endDate.toLocaleDateString()}
                          </div>
                          {shipment.eventsCount > 0 && <div className="text-green-400 mt-1">• Has tracking events</div>}
                        </div>
                      </div>
                    </div>
                    <div className="absolute right-full mr-2 text-xs text-gray-600 font-medium whitespace-nowrap">
                      {shipment.title}
                    </div>
                  </div>
                );
              })}

              {visibleShipments.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-gray-500 text-center">
                    <div className="text-lg font-medium mb-2">No shipments in this period</div>
                    <div className="text-sm">Try checking your project field mappings</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 border-t">
        <button
          onClick={scrollToToday}
          className="px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          Scroll to Today
        </button>
      </div>
    </div>
  );
};
